/* eslint-disable unicorn/consistent-destructuring */
import angular from 'angular';
import _ from 'lodash-es';

import APIV5 from '~/models/api';
import { fr_FR, en_US } from '@knitiv/locales';
import favicon from '~/favicon.ico';
import { EN_GB, EN_US, FR_FR } from '~/utils/locales';
import routes from './routes';
import modules from './modules';

import '~/utils/dayjs-setup';

/* globals Knitiv, Router */

const navbarHeader = (show = true) =>/* @ngInject */ (/** @type {Knitiv.RootScope} */ $rootScope) => {
	$rootScope.showHeader = show;
	return true;
};

const loadConfig = /* @ngInject */ (
	/** @type {import('angular').IQService} */$q,
	/** @type {Knitiv.RootScope} */$rootScope,
	/** @type {import('angular').translate.ITranslateService} */$translate,
	/** @type {any} */ Notification,
	// eslint-disable-next-line unicorn/consistent-function-scoping
) => $q(async resolve => {
	try {
		if (!$rootScope.appConfig) {
			// eslint-disable-next-line unicorn/consistent-function-scoping
			const config = await APIV5.instance.getConfig({}, {
				id: 'app.start',
			});

			if (config) {
				$rootScope.appConfig = config;
			}
		}
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.info('config', $rootScope.appConfig);
		}
		await $translate.use($rootScope.appConfig.lang.code);
	}
	catch {
		// console.error('Unable to fetch config', error);
		Notification.error({
			title:     $translate.instant('error.title'),
			message:   'Impossible de récupérer la configuration de la base depuis le serveur',
		});
	}

	/** @type {?Knitiv.AppConfigTheme} */
	const theme = $rootScope.appConfig?.theme || null;
	$rootScope.favicon = theme?.favicon ?? favicon;

	return resolve(true);
});

/**
 * @param {import('angular').ILocationService} $location
 * @param {Knitiv.RootScope} $rootScope
 * @param {import('~/scripts/services/model/reset').ResetModel} Reset
 */
const redirect = ($location, $rootScope, Reset) => {
	Reset.resetApp();
	const search = $location.search();
	if (!search.redirectTo) {
		const redirectTo = $location.url() === '/' ? '' : `?redirectTo=${$location.url()}`;
		if ($location.path() !== '/login') {
			$location.url(`/login${redirectTo}`);
		}
		else if ($location.url() !== '/login') {
			$location.search({ redirectTo: $location.url() });
		}
	}
	$rootScope.showApp = true;
	// throw new Error('Token check failed');
};

const checkToken = (skipToken = false) => /* @ngInject */ (
	/** @type {Knitiv.RootScope} */ $rootScope,
	/** @type {import('angular').ILocationService} */ $location,
	/** @type {Knitiv.UserModel} */ User,
	/** @type {import('angular').IQService} */ $q,
	/** @type {import('angular').translate.ITranslateService} */ $translate,
	/** @type {ReturnType<import('./providers/models/notification').NotificationProvider['$get']>} */ Notification,
	/** @type {import('~/scripts/services/model/reset').ResetModel} */ Reset,
	// eslint-disable-next-line unicorn/consistent-function-scoping
) => {
	/**
	 * @param {?(Knitiv.AuthenticationObject
	 * 	 & ?{config: import('@knitiv/api-client-javascript').Config})} context
	 */
	const setAPI = context => {
		const url = {
			back: '/',
			file: '/',
		}
		if (context?.url) {
			url.back = context?.url.back ?? url.back;
			url.file = context?.url.file === 'none' ? url.back : (context?.url.file ?? url.back);
		}
		else if (context?.config) {
			url.back = context?.config.BACK?.url ?? '/';
			url.file = context?.config.FILE?.url || url.back;
		}
		APIV5.instance.setBackEndpoint(url.back);
		APIV5.instance.setFileEndpoint(url.file);
		APIV5.instance.setToken(context?.token.id ?? '');
		APIV5.instance.setFrontURL($location.url());
		APIV5.instance.setFrontVersion($rootScope.tag);

		if (context) {
			const { role } = context;
			if (typeof role.role === 'object') {
				$rootScope.hasCreatePermissions = role.mode === 'ADMIN_SUPER'
					|| role.mode === 'ADMIN_SYS'
					|| 'K_NODE;KR_OWNER_ADM' in role.role;
			}
			if (context.config?.COLLABORA?.url) {
				// APIV5.instance.setCollaboraOnlineServer(context.config.COLLABORA?.url);
			}
		}
		else {
			$rootScope.hasCreatePermissions = false;
		}
	};

	// We don't yet have acces to $routeParams here, so using raw history
	const urlSearchParameters = new URLSearchParams(window.location.search);
	const parameters = Object.fromEntries(urlSearchParameters.entries());

	const protocol = $location.protocol();
	const host = $location.host();
	const port = $location.port();
	const fallback = `${protocol}://${host}:${port}`;

	const isLogin = $location.path() === '/login';

	if (parameters.url_back) {
		_.set($rootScope, 'context.url.back', parameters.url_back);

		APIV5.instance.setBackEndpoint(parameters.url_back);
		urlSearchParameters.delete('url_back');
	}
	else {
		APIV5.instance.setBackEndpoint(fallback);
	}

	if (parameters.url_file) {
		_.set($rootScope, 'context.url.file', parameters.url_file);

		APIV5.instance.setFileEndpoint(parameters.url_file);
		urlSearchParameters.delete('url_file');
	}
	else {
		APIV5.instance.setBackEndpoint(fallback);
	}

	const token = localStorage.getItem('token') ?? $rootScope.context?.token?.id;
	// If we don't have token, redirect to login
	if (
		!token && skipToken === false
		&& $location.path() !== '/login'
	) {
		return redirect($location, $rootScope, Reset);
	}

	if (token) {
		// otherwise, try to authenticate

		APIV5.instance.setToken(token);

		return APIV5.instance
			.tokenCheck(undefined, {
				id: 'page.start.check-token',
			})
			.then((/**
			  @type {Knitiv.AuthenticationObject
			 & {config: import('@knitiv/api-client-javascript').Config}} */ context) => {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.info('context', context);
				}

				setAPI(context);

				User.set(context).saveToken();
				// $rootScope.$emit('User.login');

				// eslint-disable-next-line promise/no-nesting
				return loadConfig($q, $rootScope, $translate, Notification).then(state => {
					$rootScope.showApp = true;

					if (isLogin) {
						$location.url('/');
					}

					return state;
				});
			})
		// if that failed, also redirect
			.catch(() => redirect($location, $rootScope, Reset));
	}
	$rootScope.showApp = true;
	return true;
};

/**
 * @typedef {Router.Route} Route
 */

/* @ngInject */
const configCallback = (
	/** @type {import('angular').route.IRouteProvider} */
	$routeProvider,

	/** @type {import('angular').ILocationProvider} */
	$locationProvider,

	/** @type {import('angular').IHttpProvider} */
	$httpProvider,

	/** @type {import('angular').translate.ITranslateProvider} */
	$translateProvider,

	/** @type {any} */
	ngIntlTelInputProvider,

	/** @type {any} */
	NotificationProvider,

	/** @type {import('angular').ISCEDelegateProvider} */
	$sceDelegateProvider,

	/** @type {any} */
	angularMemoryStatsProvider,
) => {
	/** @type {Array<Route>} */
	for (const route of routes) {
		const {
			path,
			skipTokenCheck,
			showHeader,
			...rest
		} = route;

		if (!rest.resolve) {
			rest.resolve = {};
		}

		rest.resolve.auth = checkToken(skipTokenCheck);
		// rest.resolve.themeConfig = loadConfig();
		rest.resolve.navbarHeader = navbarHeader(skipTokenCheck ? false : showHeader);

		const keys = Object.keys(rest.resolve);
		if (keys.length === 0) {
			delete rest.resolve;
		}

		$routeProvider.when(path, rest);
	}

	$routeProvider.when('/front5/:url*?', {
		// @ts-ignore
		redirectTo: routeParameters => {
			if (routeParameters) {
				window.open(`/front5/${routeParameters.url}`, '_self');
			}
			else {
				window.open('/front5/');
			}
		},
	});

	$routeProvider.when('/aktivnote/:url*?', {
		// @ts-ignore
		redirectTo: routeParameters => {
			if (routeParameters) {
				window.open(`/aktivnote/${routeParameters.url}`, '_self');
			}
			else {
				window.open('/aktivnote/');
			}
		},
	});

	$routeProvider.otherwise({
		redirectTo: '/',
	});

	$locationProvider.html5Mode({
		enabled: true,
		requireBase: true,
	});

	$httpProvider.interceptors.push('nrReplaceInterceptor');

	// add translation table
	$translateProvider
		.translations(EN_US, en_US)
		.translations(EN_GB, en_US)
		.translations(FR_FR, fr_FR)
		.preferredLanguage(FR_FR)
		.fallbackLanguage(FR_FR);
	// .useSanitizeValueStrategy('escape'); // do NOT uncomment this!

	ngIntlTelInputProvider.set({
		autoPlaceholder: 'off',
		initialCountry: 'fr',
		preferredCountries: ['fr'],
	});

	NotificationProvider.setOptions({
		positionY: 'bottom',
	});

	APIV5.instance.setBackEndpoint('/');
	APIV5.instance.setFileEndpoint('/');

	$sceDelegateProvider.trustedResourceUrlList([
		'self',
	]);

	angularMemoryStatsProvider.enable(process.env.NODE_ENV !== 'production');
	angularMemoryStatsProvider.setCss({
		zIndex: 100,
	});
};

const InitController = /* @ngInject */ (
	/** @type {import('~/scripts/services/model/reset').ResetModel} */ Reset,
	/** @type {import('angular').auto.IInjectorService} */ $injector,
) => {
	Reset.resetApp(true);
	APIV5.setInjector($injector);
};

/**
 * @name app
 * @description
 * # app
 *
 * Main module of the application.
 */
angular.module('app', [
	'ngAnimate',
	'ngRoute',
	'ui.bootstrap',
	'dndLists',
	'pascalprecht.translate',
	'frapontillo.bootstrap-switch',
	'ui.select',
	'angularResizable',
	'sprintf',
	'treeControl',
	'ui.sortable',
	'ovh-angular-jsplumb',
	'ngDraggable',
	'ngSanitize',
	'ui.bootstrap.contextMenu',
	'colorpicker.module',
	'ngMap',
	'angular-timeline',
	'angular.filter',
	'ngIntlTelInput',
	'angular-svg-round-progressbar',
	'ngWebSocket',
	'btford.socket-io',
	'angularMoment',
	'angular-memory-stats',
	'ngFileUpload',
	...modules,
])
	.config(configCallback)
	.run(
		/* @ngInject */
		(
			/** @type {Knitiv.RootScope} */
			$rootScope,
		) => {
			// @ts-ignore
			// eslint-disable-next-line angular/no-run-logic
			$rootScope.variables = process.env;
		})
	.controller('InitController', InitController)

	.value('THROTTLE_MILLISECONDS', 250);
